import React, { useState, useEffect } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Nullable } from 'primereact/ts-helpers';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { calculateDefaultDateRange } from '../../helpers/filters/DateRangeHelper';
import './DateRangePicker.scss';

type PeriodOption = {
  label: string;
  value: string;
};

interface DateRangePickerProps {
  value: (Date | null)[] | null;
  onChange: (range: Date[]) => void;
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  minDate?: Date;
  maxDate?: Date;
  periodOptions: PeriodOption[];
  calculateRange: (period: string) => Date[] | null;
  defaultPeriod: string;
  oldestDate?: Date;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  selectedPeriod,
  onPeriodChange,
  minDate,
  maxDate,
  periodOptions,
  calculateRange,
  defaultPeriod,
  oldestDate,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [tempPeriod, setTempPeriod] = useState<string>(selectedPeriod);
  const [tempRange, setTempRange] = useState<(Date | null)[]>(value || []);

  const handlePeriodClick = (period: string): void => {
    setTempPeriod(period);
    if (period !== 'CUSTOM') {
      const newRange = calculateRange(period);
      if (newRange) {
        setTempRange(newRange);
        onChange(newRange);
        onPeriodChange(period);
        setIsOpen(false);
      }
    }
  };

  const handleCalendarChange = (dates: Nullable<(Date | null)[]>): void => {
    setTempRange(dates || []);
    setTempPeriod('CUSTOM');
  };

  const handleApply = (): void => {
    // One click in range mode leaves the end date null — apply it as a single-day range.
    const start = tempRange?.[0];
    const end = tempRange?.[1] ?? start;
    if (start && end) {
      onChange([start, end]);
      onPeriodChange(tempPeriod);
    }
    setIsOpen(false);
  };

  const handleReset = (): void => {
    if (oldestDate) {
      const { period, range } = calculateDefaultDateRange(oldestDate);
      setTempRange(range);
      setTempPeriod(period);
      onChange(range);
      onPeriodChange(period);
      setIsOpen(false);
    } else {
      const defaultRange = calculateRange(defaultPeriod);
      if (defaultRange) {
        setTempRange(defaultRange);
        setTempPeriod(defaultPeriod);
        onChange(defaultRange);
        onPeriodChange(defaultPeriod);
        setIsOpen(false);
      }
    }
  };

  const getDisplayValue = (): string => {
    if (!value?.[0] || !value?.[1]) {
      return t('selectDateRange');
    }

    // Check if selected period matches a predefined one
    if (selectedPeriod !== 'CUSTOM') {
      const option = periodOptions.find((opt) => opt.value === selectedPeriod);
      if (option) return option.label;
    }

    return `${format(value[0], 'yyyy-MM-dd')} - ${format(value[1], 'yyyy-MM-dd')}`;
  };

  useEffect(() => {
    if (value) {
      setTempRange(value);
    }
  }, [value]);

  useEffect(() => {
    setTempPeriod(selectedPeriod);
  }, [selectedPeriod]);

  return (
    <div className="date-range-picker">
      <button type="button" className="date-range-picker__trigger" onClick={() => setIsOpen(!isOpen)}>
        <span className="ico--calendar ico--xs" />
        <span className="date-range-picker__value">{getDisplayValue()}</span>
        <span className={`ico--chevron-down ico--xs ${isOpen ? 'rotated' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="date-range-picker__overlay" onClick={() => setIsOpen(false)} />
          <div className="date-range-picker__panel">
            <div className="date-range-picker__content">
              <div className="date-range-picker__periods">
                <div className="date-range-picker__periods-title">{t('period')}</div>
                <ul className="date-range-picker__periods-list">
                  {periodOptions.map((option) => (
                    <li
                      key={option.value}
                      className={`date-range-picker__period-item ${
                        tempPeriod === option.value ? 'date-range-picker__period-item--active' : ''
                      }`}
                      onClick={() => handlePeriodClick(option.value)}
                    >
                      {option.label}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="date-range-picker__calendar-section">
                <div className="date-range-picker__custom-header">
                  <span className="date-range-picker__custom-title">{t('customDate')}</span>
                  <div className="date-range-picker__date-inputs">
                    <div className="date-range-picker__date-input">
                      <span>{tempRange[0] ? format(tempRange[0], 'yyyy-MM-dd') : '----/--/--'}</span>
                      <span className="ico--calendar ico--xs" />
                    </div>
                    <span className="date-range-picker__date-separator">→</span>
                    <div className="date-range-picker__date-input">
                      <span>{tempRange[1] ? format(tempRange[1], 'yyyy-MM-dd') : '----/--/--'}</span>
                      <span className="ico--calendar ico--xs" />
                    </div>
                  </div>
                  <ul className="date-range-picker__periods-list date-range-picker__periods-list--mobile">
                    {periodOptions.map((option) => (
                      <li
                        key={option.value}
                        className={`date-range-picker__period-item ${
                          tempPeriod === option.value ? 'date-range-picker__period-item--active' : ''
                        }`}
                        onClick={() => handlePeriodClick(option.value)}
                      >
                        {option.label}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="calendar-panel">
                  <Calendar
                    value={tempRange as Date[]}
                    onChange={(e) => handleCalendarChange(e.value as Nullable<(Date | null)[]>)}
                    selectionMode="range"
                    inline
                    numberOfMonths={1}
                    showOtherMonths={false}
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                </div>
              </div>
            </div>

            <div className="date-range-picker__footer">
              <Button
                label={t('resetToDefault')}
                className="btn btn--sm btn--outline"
                onClick={handleReset}
              />
              <Button label={t('apply')} className="btn btn--sm btn--primary" onClick={handleApply} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangePicker;
